/**
 * Pure helper that scans `lines` for matches of `searchedInput`.
 *
 * IMPORTANT: `searchedInput` is fed directly into `new RegExp(...)`. Callers
 * that derive the value from user input MUST escape it first (see
 * `escapeRegex`) — passing an unsanitized user string here can throw on
 * malformed regex characters or, with attacker-crafted input, produce
 * pathological regexes (ReDoS).
 */
type SearchedKeyWord = {
  rowIndex: number;
  matchIndex: number;
};

export const escapeRegex = (input: string): string =>
  input.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

export const searchForKeyword = (
  searchedInput: string,
  lines: string[],
  itemCount: number
): SearchedKeyWord[] => {
  const searchResults: SearchedKeyWord[] = [];
  let regex: RegExp;
  try {
    regex = new RegExp(searchedInput, 'ig');
  } catch {
    // Defensive: if the caller forgot to escape, return an empty result
    // rather than throwing.
    return [{ rowIndex: -1, matchIndex: 0 }];
  }

  for (let index = 0; index < lines.length && index < itemCount; index++) {
    const row = lines[index];
    const matches = row.match(regex);
    if (matches) {
      for (let i = 1; i <= matches.length; i++) {
        searchResults.push({ rowIndex: index, matchIndex: i });
      }
    }
  }

  if (searchResults.length > 0) {
    return searchResults;
  }

  return [{ rowIndex: -1, matchIndex: 0 }];
};

export type { SearchedKeyWord };
